<template>
  <div class="space-y-20">
    <div v-if="vacancy?.id || vacancyLoading">
      <dynamic-style
        v-if="vacancy?.sector"
        :color="vacancy?.sector?.kleur ?? '#7270C2'"
      />
      <section>
        <div class="relative">
          <div class="card-base absolute inset-0 z-0 flex items-end overflow-hidden bg-dynamic lg:inset-x-6 lg:-bottom-1/4 lg:bg-cover lg:bg-bottom lg:bg-no-repeat">
            <patterns-marble
              v-if="vacancy?.sector?.secundaire_kleur"
              :style="{color: vacancy.sector.secundaire_kleur}"
            />
          </div>
          <div class="container relative space-y-10 py-10 lg:pb-20">
            <BaseSpinner
              v-if="vacancyLoading"
              label="Bezig met het ophalen van vacature..."
            />
            <div class="items-center justify-between lg:flex">
              <vacancy-metadata
                :vacancy="vacancy"
                :detail-page="true"
              />
              <div class="justify-right flex flex-col gap-2">
                <button
                  type="button"
                  class="btn btn-primary btn-md hidden text-center lg:block"
                  @click="goToApplicationForm"
                >
                  Direct solliciteren
                </button>
              </div>
            </div>
            <h1 class="text-2xl text-white lg:text-3xl">
              {{ vacancy.title }}
            </h1>
            <div class="align-left flex flex-col gap-2">
              <button
                type="button"
                class="btn btn-sm btn-primary block text-center lg:hidden"
                @click="goToApplicationForm"
              >
                Direct solliciteren
              </button>
              <br>
            </div>
          </div>
        </div>
      </section>
      <section class="container relative grid-cols-10 px-4 lg:grid lg:gap-x-6">
        <div class="rounded-xl bg-dynamic lg:col-span-6">
          <div class="h-full rounded-xl bg-white/90 px-4 py-6 lg:p-20">
            <h3 class="text-xl font-medium leading-10 text-dynamic lg:text-2xl">
              Over de functie
            </h3>
            <SofieText :value="vacancy.description" />
            <template v-if="vacancy.company_description">
              <h3 class="mt-6 text-xl font-medium leading-10 text-dynamic lg:text-2xl">
                Jouw nieuwe werkplek
              </h3>
              <SofieText :value="vacancy.company_description" />
            </template>
            <div class="my-8 rounded-xl bg-white p-8 lg:mb-6 lg:mt-16">
              <h2
                class="mb-6 text-2xl font-medium leading-9"
                :style="{color: vacancy?.sector?.secundaire_kleur ?? '#7270C2'}"
              >
                Dit bieden wij jou
              </h2>
              <SofieText :value="vacancy.conditions" />
            </div>
            <div
              v-if="vacancy.requirements"
              class="my-8 rounded-xl bg-white p-8"
            >
              <h2
                class="mb-6 text-xl leading-8 lg:text-2xl lg:font-medium lg:leading-9"
                :style="{color: vacancy?.sector?.secundaire_kleur ?? '#7270C2'}"
              >
                Dit vragen wij van jou
              </h2>
              <SofieText :value="vacancy.requirements" />
            </div>
            <div
              v-if="vacancy.procedure"
              class="my-8"
            >
              <h3 class="text-xl font-medium leading-10 text-dynamic lg:text-2xl">
                Solliciteren
              </h3>
              <SofieText :value="vacancy.procedure" />
            </div>
            <!--            <SocialLinksContainer />-->
          </div>
        </div>
        <div class="flex-col space-y-4 lg:col-span-4 lg:flex lg:gap-y-6 lg:space-y-0">
          <div class="container-overflow container-overflow-clear mt-4 lg:mt-0">
            <div class="w-full rounded-2xl bg-dynamic">
              <ApplicationsForm
                form-id="apply"
                :vacancy="vacancy"
                :recruiter-id="vacancy.recruiter?.id"
              />
            </div>
          </div>
          <RecruiterTile
            v-if="vacancy.recruiter"
            large
            class="bg-white lg:order-first"
            :recruiter="vacancy.recruiter"
          />
        </div>
      </section>
    </div>
    <div v-else>
      <section>
        <div class="relative lg:mx-6">
          <div class="container relative space-y-10 py-10 lg:pb-20">
            Vacature niet gevonden
          </div>
        </div>
      </section>
    </div>
    <vacancy-similar-vacancies
      v-if="vacancy?.id"
      :vacancy="vacancy"
    />
    <EmployerCarousel class="container-overflow my-20" />
  </div>
</template>

<script setup lang="ts">
import {onMounted} from 'vue';
import {useGtm} from '@gtm-support/vue-gtm';
import vacancyQuery from '~/graphql/queries/vacancy.gql';
import {type Vacancy} from '~/graphql/graphql';

definePageMeta({
  header: {
    transparent: true,
  },
});

const route = useRoute();
const router = useRouter();

if (!route.params?.slug) {
  router.push({name: 'vacatures'});
}

const state = ref({
  vacancyQuery: {
    slug: route.params.slug,
  },
});

const {result: vacancyResult, loading: vacancyLoading, onResult} = useQuery(vacancyQuery, state.value.vacancyQuery);
const vacancy = computed<Vacancy>(() => vacancyResult.value?.vacancy?.[0] ?? []);

if (vacancy?.value?.ms_job_number === route.params.slug) {
  router.push({params: {slug: vacancy.value.slug}});
}

onMounted(() => {
  sendGtmEvent();
});

onResult(() => {
  sendGtmEvent();
});

function goToApplicationForm() {
  const element = document.getElementById('apply');
  if (!element) {
    return;
  }

  element.scrollIntoView({behavior: 'smooth'});
}

const gtmEventSent = ref(false);

function sendGtmEvent() {
  const gtm = useGtm();

  if (gtmEventSent.value || !gtm || !vacancy.value?.id) {
    return;
  }

  gtmEventSent.value = true;
  gtm?.trackEvent({
    event: 'view_item',
    ecommerce: {
      items: [
        {
          item_name: vacancy.value.title,
          item_id: vacancy.value.id,
          item_brand: vacancy.value.client || 'Unknown',
          item_category: vacancy.value.location?.title || vacancy.value.city || 'Unknown',
          item_category2: vacancy.value.sector?.title || 'Unknown',
          item_category3: vacancy.value.recruiter?.department?.title || 'Unknown',
        },
      ],
    },
  });
}
</script>
